/**
 * CrmPeriodFilter - Filtro de período para dashboard CRM
 * 
 * Permite selecionar presets de período ou datas customizadas.
 * Suporta tamanho compacto via className.
 * 
 * @author Sistema
 * @version 1.1.0 - Suporte a tamanho compacto (40% menor)
 * RELEASE: 2026-10-05 v1.2.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 */

import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import type { PeriodPreset, PeriodFilter } from '@/hooks/useCrmOverviewFilters';

/**
 * Props do componente CrmPeriodFilter
 * @param value - Valor atual do filtro de período
 * @param onPresetChange - Callback para mudança de preset
 * @param onCustomChange - Callback para período customizado
 * @param className - Classes CSS adicionais (suporta h-[22px] para versão compacta)
 */
interface CrmPeriodFilterProps {
  value: PeriodFilter;
  onPresetChange: (preset: PeriodPreset) => void;
  onCustomChange: (startDate: Date, endDate: Date) => void;
  className?: string;
}

// Opções de período predefinidas
const PERIOD_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7days', label: 'Últimos 7 dias' },
  { value: '15days', label: 'Últimos 15 dias' },
  { value: 'month', label: 'Mês Atual' },
  { value: 'lastMonth', label: 'Mês Passado' },
];

/**
 * Componente de filtro de período
 */
export function CrmPeriodFilter({
  value,
  onPresetChange,
  onCustomChange,
  className,
}: CrmPeriodFilterProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: value.preset === 'custom' ? value.startDate : undefined,
    to: value.preset === 'custom' ? value.endDate : undefined,
  });

  // Label do período selecionado
  const getLabel = () => {
    if (value.preset === 'custom') {
      return `${format(value.startDate, 'dd/MM/yy')} - ${format(value.endDate, 'dd/MM/yy')}`;
    }
    return PERIOD_OPTIONS.find(o => o.value === value.preset)?.label || 'Mês Atual';
  };

  // Handler para seleção de preset
  const handlePresetSelect = (preset: PeriodPreset) => {
    if (preset === 'custom') {
      setShowCustom(true);
    } else {
      onPresetChange(preset);
      setOpen(false);
      setShowCustom(false);
    }
  };

  // Handler para confirmar período customizado
  const handleCustomConfirm = () => {
    if (customRange.from && customRange.to) {
      onCustomChange(customRange.from, customRange.to);
      setOpen(false);
      setShowCustom(false);
    }
  };

  // Handler para cancelar período customizado
  const handleCustomCancel = () => {
    setShowCustom(false);
    setCustomRange({ from: undefined, to: undefined });
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          <CalendarIcon className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
          <span className="hidden sm:inline truncate max-w-[90px]">{getLabel()}</span>
          <span className="sm:hidden truncate max-w-[60px]">{value.preset === 'custom' ? 'Custom' : getLabel().split(' ')[0]}</span>
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {!showCustom ? (
          <Command>
            <CommandList>
              <CommandGroup heading="Período">
                {PERIOD_OPTIONS.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handlePresetSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 h-4 w-4 rounded-full border",
                        value.preset === option.value
                          ? "border-primary bg-primary"
                          : "border-muted"
                      )}
                    >
                      {value.preset === option.value && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                        </div>
                      )}
                    </div>
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value="custom"
                  onSelect={() => handlePresetSelect('custom')}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border",
                      value.preset === 'custom'
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value.preset === 'custom' && (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                      </div>
                    )}
                  </div>
                  <CalendarIcon className="h-4 w-4 mr-2 opacity-70" />
                  Personalizado...
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Selecione o período</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleCustomCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Calendar
              mode="range"
              selected={{ from: customRange.from, to: customRange.to }}
              onSelect={(range) => setCustomRange({ from: range?.from, to: range?.to })}
              numberOfMonths={1}
              locale={ptBR}
              className="rounded-md border"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCustomCancel}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleCustomConfirm}
                disabled={!customRange.from || !customRange.to}
              >
                Aplicar
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
